import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
} from 'react';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
  type BaseFormFieldNodeProperties,
  type MasonryEngineNodeAction,
} from '@leagueplatform/masonry-engine';
import { Checkbox } from '@leagueplatform/genesis-core';
import { formatClassNames } from '#utils/format-masonry-class-name.util.ts';
import { useInvokeAction } from '../../../hooks/use-invoke-action.hook';

import { MasonryFieldsetWrapper } from '../masonry-fieldset-wrapper/masonry-fieldset-wrapper.component';
import { MasonryFormFieldWrapper } from '../masonry-form-field-renderer/masonry-form-field-wrapper.component';

type MasonryCheckboxOption = {
  label: string;
  value: string;
  disabled?: boolean;
  hint?: string;
  tooltip?: string;
  exclusive?: boolean;
};

type MasonryCheckboxNodeActions = {
  onChange?: MasonryEngineNodeAction;
};

type MasonryCheckboxNodeSections = {};
type MasonryCheckboxNodeProperties = BaseFormFieldNodeProperties<string[]> & {
  layout?: 'inline' | 'row';
  disabled?: boolean;
  options: MasonryCheckboxOption[];
};

export type MasonryCheckboxNode = MasonryEngineNode<
  'checkboxGroup',
  MasonryCheckboxNodeProperties,
  MasonryCheckboxNodeActions,
  MasonryCheckboxNodeSections
>;

export type MasonryCheckboxRendererProps =
  MasonryEngineNodeRendererProps<MasonryCheckboxNode>;

export const MasonryCheckboxRenderer = ({
  formFieldProperties,
  value: initialValue,
  layout,
  options,
  disabled,
  rootElementRef,
  onChange,
  className,
  engineClassName,
  locatorId,
  css,
}: MasonryCheckboxRendererProps) => {
  const { formFieldId, hint: fieldHint } = formFieldProperties;

  // Computed Props
  const computedValue = useMemo(() => {
    if (initialValue?.length) return initialValue;
    return [];
  }, [initialValue]);

  const exclusiveValues = options
    .filter((option) => option.exclusive)
    .map((option) => option.value);
  const orientation = layout === 'inline' ? 'row' : 'column';

  const invokeAction = useInvokeAction();

  // Event Handlers
  const setNodeState = useCallback(
    async <TEvent extends SyntheticEvent>(
      event: TEvent,
      nextValue: string[],
    ) => {
      const actionParams = {
        event,
        namespace: 'default',
        payload: {
          state: {
            value: nextValue,
          },
        },
      };

      await invokeAction({ type: 'setNodeState', ...actionParams });

      if (onChange) {
        invokeAction({ ...onChange, event });
      }
    },
    [invokeAction, onChange],
  );

  const changeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value: targetValue, checked, dataset } = event.currentTarget;

      // If input was unchecked, update local + global state
      if (!checked) {
        const nextValue = computedValue.filter(
          (checkedValue) => checkedValue !== targetValue,
        );
        return setNodeState(event, nextValue);
      }

      // If input is checked and exclusive, uncheck other options
      if (dataset.exclusive === 'true') {
        return setNodeState(event, [targetValue]);
      }

      // Else, add the current options and deselect any that are exclusive
      const nextValue = computedValue
        .concat(targetValue)
        .filter((selectedValue) => !exclusiveValues.includes(selectedValue));

      return setNodeState(event, nextValue);
    },
    [computedValue, exclusiveValues, setNodeState],
  );

  const Wrapper = useMemo(
    () =>
      options?.length > 1 ? MasonryFieldsetWrapper : MasonryFormFieldWrapper,
    [options?.length],
  );

  return (
    <Wrapper
      formFieldProperties={formFieldProperties}
      hint={fieldHint}
      rootElementRef={rootElementRef}
      disabled={disabled}
      css={{
        '.GDS-input-wrapper': { display: 'flex', flexDirection: orientation },
        ...css,
      }}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
    >
      {options?.map((option, optionIndex) => {
        const { label, value, exclusive, hint } = option;
        const optionId = `${formFieldId}-${optionIndex}`;
        return (
          <Checkbox
            key={optionId}
            id={optionId}
            name={formFieldId}
            label={label}
            value={value}
            hint={hint}
            disabled={option.disabled}
            checked={computedValue?.includes(value?.toString())}
            onChange={changeHandler}
            data-exclusive={Boolean(exclusive)}
            layout={layout}
          />
        );
      })}
    </Wrapper>
  );
};
